import { useState } from 'react';
import type { Staff, TimeOffReason } from '@salonomy/types';
import { Button, Field, Modal, SegmentedControl, Select, TextArea, TextInput } from '@salonomy/ui';
import { useCreateTimeOff, useSetHourException } from '../../data/scheduling.js';
import { spanLabel } from '../../lib/time.js';
import { useToast } from '../../lib/toast.js';

type Mode = 'off' | 'hours';

const REASONS: Array<{ value: TimeOffReason; label: string }> = [
  { value: 'holiday', label: 'Holiday' },
  { value: 'sick', label: 'Sick' },
  { value: 'training', label: 'Training' },
  { value: 'other', label: 'Other' },
];

/**
 * WS7 diary quick action — "Block out…" for one staff member on the viewed date. Two simple-but-real
 * paths: book a day (or range) of time off, or set a date-specific hour exception (off this date, or
 * custom hours with an optional break). Time-off surfaces any clashing appointments (the API returns
 * them as a warning); the rota editor in Settings is the full surface, this is the in-diary shortcut.
 */
export function BlockOutModal({
  date,
  tz,
  staff,
  onClose,
}: {
  date: string;
  tz: string;
  staff: Staff;
  onClose: () => void;
}) {
  const toast = useToast();
  const createTimeOff = useCreateTimeOff(staff.id);
  const setException = useSetHourException(staff.id);

  const [mode, setMode] = useState<Mode>('off');

  // Time off
  const [startDate, setStartDate] = useState(date);
  const [endDate, setEndDate] = useState(date);
  const [reason, setReason] = useState<TimeOffReason>('holiday');
  const [note, setNote] = useState('');
  const [conflicts, setConflicts] = useState<
    { id: string; clientDisplayName: string; startAt: string; endAt: string }[] | null
  >(null);

  // Hour exception (this date only)
  const [excMode, setExcMode] = useState<'closed' | 'custom'>('closed');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [breakStart, setBreakStart] = useState('');
  const [breakEnd, setBreakEnd] = useState('');

  const busy = createTimeOff.isPending || setException.isPending;

  async function submitTimeOff() {
    if (endDate < startDate) {
      toast.error('End date can’t be before the start date.');
      return;
    }
    try {
      const result = await createTimeOff.mutateAsync({
        startDate,
        endDate,
        reason,
        note: note.trim() || null,
      });
      if (result.conflicts.length > 0) {
        // Don't hide them — keep the modal open and list what overlaps so staff can rebook/notify.
        setConflicts(result.conflicts);
        toast.error(
          `${result.conflicts.length} booking${result.conflicts.length === 1 ? '' : 's'} fall in this period — rebook or notify.`,
        );
        return;
      }
      toast.success(`${staff.displayName} booked off`);
      onClose();
    } catch {
      toast.error('Could not book this time off.');
    }
  }

  async function submitException() {
    try {
      if (excMode === 'closed') {
        await setException.mutateAsync({ date, isClosed: true });
      } else {
        if (endTime <= startTime) {
          toast.error('End time must be after the start time.');
          return;
        }
        const hasBreak = breakStart && breakEnd;
        if (hasBreak && breakEnd <= breakStart) {
          toast.error('Break end must be after the break start.');
          return;
        }
        await setException.mutateAsync({
          date,
          isClosed: false,
          startTime,
          endTime,
          breakStart: hasBreak ? breakStart : null,
          breakEnd: hasBreak ? breakEnd : null,
        });
      }
      toast.success('Hours updated for this date');
      onClose();
    } catch {
      toast.error('Could not update the hours.');
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Block out — ${staff.displayName}`}
      width={460}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={mode === 'off' ? submitTimeOff : submitException} disabled={busy}>
            {mode === 'off' ? 'Book time off' : 'Save hours'}
          </Button>
        </>
      }
    >
      <SegmentedControl<Mode>
        options={[
          { value: 'off', label: 'Time off' },
          { value: 'hours', label: 'Adjust hours' },
        ]}
        value={mode}
        onChange={(m) => {
          setMode(m);
          setConflicts(null);
        }}
      />

      {mode === 'off' ? (
        <div style={{ marginTop: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label="From">
              <TextInput
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </Field>
            <Field label="To">
              <TextInput type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </Field>
          </div>
          <Field label="Reason">
            <Select value={reason} onChange={(e) => setReason(e.target.value as TimeOffReason)}>
              {REASONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Note">
            <TextArea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Optional — visible to the team"
            />
          </Field>

          {conflicts && conflicts.length > 0 ? (
            <div className="conflict-list">
              <div className="conflict-list__head">
                {conflicts.length} booking{conflicts.length === 1 ? '' : 's'} in this period —
                rebook or notify before relying on this.
              </div>
              {conflicts.map((c) => (
                <div key={c.id} className="conflict-list__row">
                  <span style={{ fontWeight: 600 }}>{c.clientDisplayName || 'Walk-in'}</span>
                  <span className="muted" style={{ fontSize: 12 }}>
                    {spanLabel(c.startAt, c.endAt, tz)}
                  </span>
                </div>
              ))}
              <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                The time off is saved — these appointments are not cancelled. Handle them, then
                close.
              </div>
            </div>
          ) : null}
        </div>
      ) : (
        <div style={{ marginTop: 14 }}>
          <SegmentedControl<'closed' | 'custom'>
            options={[
              { value: 'closed', label: 'Off this date' },
              { value: 'custom', label: 'Custom hours' },
            ]}
            value={excMode}
            onChange={setExcMode}
          />
          {excMode === 'custom' ? (
            <div style={{ marginTop: 12 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <Field label="Start">
                  <TextInput
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                  />
                </Field>
                <Field label="End">
                  <TextInput
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                  />
                </Field>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <Field label="Break from" hint="Optional">
                  <TextInput
                    type="time"
                    value={breakStart}
                    onChange={(e) => setBreakStart(e.target.value)}
                  />
                </Field>
                <Field label="Break to">
                  <TextInput
                    type="time"
                    value={breakEnd}
                    onChange={(e) => setBreakEnd(e.target.value)}
                  />
                </Field>
              </div>
            </div>
          ) : (
            <div className="muted" style={{ fontSize: 12.5, marginTop: 12 }}>
              {staff.displayName} will be marked off for this date only. Use “Time off” for a range.
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
